import { useState, useEffect } from "react";
import { Button } from "../../ui/Button/Button"
import { Text } from "../../ui/Text/Text"
import { getEditModeItemsQuantity, getEditModeLineItems, getLineItems, stopEditingMode } from "./utils";
import { getSingleProductVariantFromMainProductByLengthInTitle, isEditMode, isPos } from "../../../utils";
import { useApp } from "../../../hooks/useApp";

interface Props {
  className?: string
}

export const AddToCartButton = ({ className}: Props) => {
  const { mainProduct, activeMainProductVariantId, activePendantProductVaraintIds, cart, isMainProductSinglePendant} = useApp()
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error" | "copy" | "copied">(
      "idle"
    );

  useEffect(() => {
    if (isPos()) {
      setStatus("copy")
    }
  }, [])

  async function copyToClipboard(text: string) {
    try {
      await navigator.clipboard.writeText(text);
    } catch (e) {
      console.error(e);
    }
  }

  

  const handleAddToCart = async () => {
    // 3 cases:
    // 1. Multiple pendants mainProduct:
    // 1.1. More than 1 pendant variant -> add chain product variant and pendant variants
    // 1.2. Only 1 pendant variant -> add linked single product variant
    // 2. Single pendant mainProduct 
    // 2.1. Add main product variant depending on selected charm and length

    setStatus("loading");

    const quantity = isEditMode() ? getEditModeItemsQuantity() : 1
    
    const url = new URL(window.location.href);
    url.searchParams.delete("edit_qts");
    url.searchParams.delete("edit_mode");
    url.searchParams.delete("edit_q");

    const bundleUrl = url.toString();

    let itemsToAdd = getLineItems(mainProduct, activeMainProductVariantId, activePendantProductVaraintIds, quantity, bundleUrl, isMainProductSinglePendant)
    const itemsToRemove = getEditModeLineItems(mainProduct, cart)

    // @TODO Extract to the function above
    if (activePendantProductVaraintIds.length === 1 && !isMainProductSinglePendant) {
      const linkedSingleProductVariant = getSingleProductVariantFromMainProductByLengthInTitle(
        mainProduct, 
        Number(activeMainProductVariantId),
        Number(activePendantProductVaraintIds[0])
      )
      
      if (linkedSingleProductVariant?.available) {
        itemsToAdd = [
          {
            id: String(linkedSingleProductVariant.id),
            quantity: 1,
            properties: {
              _url: bundleUrl
            }
          }
        ]
      }
    }
  
    const urlParams = new URLSearchParams(window.location.search);

    if (urlParams.has("pos")) {
      const formattedItems = itemsToAdd.map(itemToAdd => {
        const { id, ...rest } = itemToAdd;
        return { variantId: Number(id), ...rest };
      });

      copyToClipboard(JSON.stringify(formattedItems));
      setStatus("copied");
      setTimeout(() => setStatus("copy"), 5000)
      return;
    }

    const requestOptions = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
    }

    const addItems = () => fetch("/cart/add.js", { 
      ...requestOptions,
      body: JSON.stringify({ items: itemsToAdd }),
    })

    const removeItems = () => fetch("/cart/update.js", { 
      ...requestOptions,
      body: JSON.stringify({ updates: itemsToRemove }),
    })

    Promise.all([
      addItems(), 
      itemsToRemove && removeItems()
    ])
      .then(res => {
        const [addItemsRes, removeItemsRes] = res;
        if (!addItemsRes.ok || removeItemsRes !== null && !removeItemsRes.ok) throw new Error("Failed to update cart")
      })
      .then(() => {
        setStatus("success");

        document.dispatchEvent(
          new CustomEvent('product:added', {
            bubbles: true,
            detail: {
              variant: {
                name: mainProduct.title
              },
              quantity
            }
          })
        )

        isEditMode() && stopEditingMode()

        setTimeout(() => setStatus("idle"), 2000);
      })
      .catch((err) => {
         console.error(err);
        setStatus("error");
        setTimeout(() => setStatus("idle"), 2000);
      })
  };

  return (
    <Button className={className} size="md" color="black" disabled={status === "loading"} onClick={handleAddToCart}>
      <Text style="label">
        {status === "loading"
          ? "•••"
          : status === "error"
          ? "Error"
          : status === "copied"
          ? "Copied"
          : status === "copy"
          ? "Copy IDs"
          : isEditMode()
          ? "Edit"
          : "Add to Bag"
        }
      </Text>
    </Button>
  )
}